import { activeSiteConfig } from "@config/site/site-config";
import type {
    BaseMoney,
    LineItem,
    Maybe,
    ProductPrice,
    RawCustomField,
} from "@graphql/generated/types";
import { ExtraPriceType } from "@lib/constants/filtersConstants";
import { CustomAttributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "./attributesUtils";

export const isOmniBusMarket = (): boolean => {
    return activeSiteConfig?.extraPriceType?.includes(ExtraPriceType.OMNIBUS) ?? false;
};

const isMoney = (value: unknown): value is BaseMoney =>
    typeof (value as BaseMoney | undefined)?.centAmount === "number";

const getMoneyField = (
    customFields: Maybe<RawCustomField[]> | undefined,
    name: CustomAttributes
): BaseMoney | undefined => {
    const value: unknown = getProductAttributeValue(customFields ?? [], name);
    return isMoney(value) ? value : undefined;
};

/** Discount against the 30-day lowest price, else the normal discount. */
export const getOmnibusDiscountPercentage = (
    customFields: Maybe<RawCustomField[]> | undefined
): number | undefined => {
    const hasPreviousLowestPrice = !!getMoneyField(
        customFields,
        CustomAttributes.PREVIOUS_LOWEST_PRICE
    )?.centAmount;
    const discount: unknown = getProductAttributeValue(
        customFields ?? [],
        hasPreviousLowestPrice
            ? CustomAttributes.DISCOUNT_PERCENTAGE_PREVIOUS_LOWEST_PRICE
            : CustomAttributes.DISCOUNT_PERCENTAGE
    );
    return typeof discount === "number" ? discount : undefined;
};

type OmnibusPrice = Pick<ProductPrice, "value" | "discounted" | "custom">;

const getSellingPrice = (price?: OmnibusPrice): BaseMoney | undefined =>
    price?.discounted?.value ?? price?.value;

/**
 * Unit price an on-sale product may be struck through against: the 30-day lowest price,
 * falling back to RRP. Undefined when it is not above the price the product sells at.
 */
const getOmnibusReferencePrice = (price?: OmnibusPrice): BaseMoney | undefined => {
    const customFields = price?.custom?.customFieldsRaw;
    const discountPercentage: unknown = getProductAttributeValue(
        customFields ?? [],
        CustomAttributes.DISCOUNT_PERCENTAGE
    );
    if (typeof discountPercentage !== "number" || discountPercentage <= 0) return undefined;

    const previousLowestPrice = getMoneyField(customFields, CustomAttributes.PREVIOUS_LOWEST_PRICE);
    const referencePrice = previousLowestPrice?.centAmount
        ? previousLowestPrice
        : getMoneyField(customFields, CustomAttributes.RECOMMENDED_PRICE);
    const sellingCentAmount = getSellingPrice(price)?.centAmount;
    if (
        !referencePrice ||
        sellingCentAmount === undefined ||
        referencePrice.centAmount <= sellingCentAmount
    ) {
        return undefined;
    }
    return referencePrice;
};

/**
 * Unit price a line is compared against: the omnibus reference price, else the price the product
 * sells at before promo codes. Never the base price of a product on sale. Shared by the line
 * strikethrough, the summary subtotal and the sale discount row so they all reconcile.
 */
export const getOmnibusComparisonPrice = (price?: OmnibusPrice): BaseMoney | undefined =>
    getOmnibusReferencePrice(price) ?? getSellingPrice(price);

/**
 * CT's subtotal sums each line's line-item_CF_Price, which is the RRP for sale items. This is
 * the change from swapping that for the omnibus reference price, or the selling price when
 * there is none, so it is usually negative.
 */
export const getOmnibusSubtotalAdjustmentCentAmount = (lineItems: LineItem[]): number =>
    lineItems.reduce((acc, item) => {
        const originalLinePrice = getMoneyField(
            item.custom?.customFieldsRaw,
            CustomAttributes.LINE_ITEM_PRICE
        );
        const unitCentAmount = getOmnibusComparisonPrice(item.price)?.centAmount;
        if (!originalLinePrice || unitCentAmount === undefined) return acc;

        return acc + unitCentAmount * item.quantity - originalLinePrice.centAmount;
    }, 0);
